import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  FormGroup,
  Stack,
  Switch,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import theme from "../../../theme";
import CheckIcon from "@mui/icons-material/Check";
import {
  LIVE_PRICES,
  AUTOMATED_PRICES,
  LIVE_AUTO_PRICES,
  ALL_IN_ONE_PRICES,
  PricingAddons,
  PRICING_GHLEvent,
} from "./pricingData";
import { config } from "../../../config";
import { decodeMetadataToken } from "../../../utils/token.utils";

interface IPlan {
  planName: string;
  monthly: number;
  annualPlanPerMonth: number;
  annualMaxPrice: number;
  savingsAmount?: number;
  addons?: string[];
  liveAttendees?: number;
  automatedAttendees?: number;
  attendees?: number;
}

const planFeaturesList: any = (isYearly?: boolean, isGHLEvent?: boolean) => {
  return {
    "50": [
      {
        title: "What's Included",
        list: [
          {
            label: "AI Webinar Builder",
            tooltip:
              "Create webinar pages, flows, and presentations instantly using AI — no design or technical setup required.",
          },
          {
            label: "Create up to 2 AI webinar funnels",
            tooltip:
              "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking.",
          },
          { label: "Live webinars", tooltip: "" },
          { label: "Up to 5 live webinars", tooltip: "" },
          { label: "Standard support", tooltip: "" },
        ],
      },
    ],
    "200": [
      {
        title: "What's Included",
        list: [
          {
            label: "AI Webinar Builder",
            tooltip:
              "Create webinar pages, flows, and presentations instantly using AI — no design or technical setup required.",
          },
          {
            label: "Create up to 5 AI webinar funnels",
            tooltip:
              "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking.",
          },
          { label: "Live webinars", tooltip: "" },
          {
            label: "Automated / Simulive",
            tooltip:
              "Run pre-recorded webinars at scheduled times that feel live — with real-time chat, timed offers, and presenter-style experiences for higher engagement.",
          },
          {
            label: "Evergreen",
            tooltip:
              "Create always-on webinar funnels that run 24/7, capture leads continuously, and drive conversions at scale with deadline-based automation.",
          },
          { label: "Unlimited webinars", tooltip: "" },
          { label: "Stream to LinkedIn, FB, YouTube /RTMP", tooltip: "" },
          {
            label: "Paid webinars",
            tooltip:
              "Sell access to your webinars using built-in payments. Add-on on monthly plans · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          {
            label: "Built-in Sales CRM (EasyCRM)",
            tooltip:
              "Capture leads, automate follow-ups, and track deals inside EasyWebinar. $49/month add-on · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          { label: "Priority support + Manager", tooltip: "" },
        ],
      },
    ],
    "500": [
      {
        title: "What's Included",
        list: [
          {
            label: "AI Webinar Builder",
            tooltip:
              "Create webinar pages, flows, and presentations instantly using AI — no design or technical setup required.",
          },
          {
            label: "Create up to 10 AI webinar funnels",
            tooltip:
              "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking.",
          },
          { label: "Live webinars", tooltip: "" },
          {
            label: "Automated / Simulive",
            tooltip:
              "Run pre-recorded webinars at scheduled times that feel live — with real-time chat, timed offers, and presenter-style experiences for higher engagement.",
          },
          {
            label: "Evergreen",
            tooltip:
              "Create always-on webinar funnels that run 24/7, capture leads continuously, and drive conversions at scale with deadline-based automation.",
          },
          { label: "Unlimited webinars", tooltip: "" },
          { label: "Stream to LinkedIn, FB, YouTube /RTMP", tooltip: "" },
          {
            label: "Paid webinars",
            tooltip:
              "Sell access to your webinars using built-in payments. Add-on on monthly plans · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          {
            label: "Built-in Sales CRM (EasyCRM)",
            tooltip:
              "Capture leads, automate follow-ups, and track deals inside EasyWebinar. $49/month add-on · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          { label: "Priority support + Manager", tooltip: "" },
        ],
      },
    ],
    "3000": [
      {
        title: "What's Included",
        list: [
          { label: "3 Active Evergreen Funnels", tooltip: "" },
          {
            label: "Create up to 2 AI webinar funnels",
            tooltip:
              "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking.",
          },
          {
            label: "Paid webinars",
            tooltip:
              "Sell access to your webinars using built-in payments. Add-on on monthly plans · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          {
            label: "Built-in Sales CRM (EasyCRM)",
            tooltip:
              "Capture leads, automate follow-ups, and track deals inside EasyWebinar. $49/month add-on · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          { label: "Standard support", tooltip: "" },
        ],
      },
    ],
    "6000": [
      {
        title: "What's Included",
        list: [
          { label: "6 Active Evergreen Funnels", tooltip: "" },
          {
            label: "Create up to 10 AI webinar funnels",
            tooltip:
              "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking.",
          },
          {
            label: "Paid webinars",
            tooltip:
              "Sell access to your webinars using built-in payments. Add-on on monthly plans · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          {
            label: "Built-in Sales CRM (EasyCRM)",
            tooltip:
              "Capture leads, automate follow-ups, and track deals inside EasyWebinar. $49/month add-on · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          { label: "Priority support", tooltip: "" },
        ],
      },
    ],
    "10000": [
      {
        title: "What's Included",
        list: [
          { label: "10 Active Evergreen Funnels", tooltip: "" },
          {
            label: "Create up to 20 AI webinar funnels",
            tooltip:
              "Each AI webinar funnel includes a registration page, live or automated webinar, email follow-ups, and conversion tracking.",
          },
          {
            label: "Paid webinars",
            tooltip:
              "Sell access to your webinars using built-in payments. Add-on on monthly plans · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          {
            label: "Built-in Sales CRM (EasyCRM)",
            tooltip:
              "Capture leads, automate follow-ups, and track deals inside EasyWebinar. $49/month add-on · Free with annual plans.",
            tag: !isGHLEvent && isYearly ? "free" : "+$49/mo add-on",
          },
          { label: "Priority support + Manager", tooltip: "" },
        ],
      },
    ],
  };
};

// AntSwitch component from SubscriptionDetails
const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  display: "flex",
  borderRadius: 50,
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(24px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    top: 1,
    "&.Mui-checked": {
      transform: "translateX(19px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#22B060",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 18,
    height: 18,
    borderRadius: 24,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "#22B060",
    boxSizing: "border-box",
  },
}));

interface PricingSectionProps {
  currentPlanId?: string;
  currentAttendees?: number;
  onPlanSelect: (planId: string) => void;
  hideTabs?: boolean;
  isSaleLive?: boolean;
  id?: string;
  subscriptionStatus?: string;
}

// Main component
const PricingSectionV2: React.FC<PricingSectionProps> = ({
  currentPlanId,
  currentAttendees,
  onPlanSelect,
  hideTabs = false,
  isSaleLive,
  id,
  subscriptionStatus,
}) => {
  // Use config.isSaleLive as default if prop is not provided
  const saleLive = isSaleLive ?? config.isSaleLive;
  const [isGHLEvent, setIsGHLEvent] = useState(false);
  // State
  const [planDuration, setPlanDuration] = useState<"monthly" | "yearly">(
    "yearly",
  );
  const [tabValue, setTabValue] = useState(0);

  const [liveValue, setLiveValue] = useState("50");
  const [automatedValue, setAutomatedValue] = useState("3000");
  const [liveAutomatedValue, setLiveAutomatedValue] = useState("200");
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [categoryTab, setCategoryTab] = useState<0 | 1>(0);
  const [selectedAddonsByPlan, setSelectedAddonsByPlan] = useState<
    Record<string, string[]>
  >({});
  const [durationByPlanKey, setDurationByPlanKey] = useState<
    Record<string, "monthly" | "yearly">
  >({});

  // Auto-detect current plan configuration
  useEffect(() => {
    if (currentPlanId && currentAttendees) {
      // Set plan duration based on current plan
      if (currentPlanId.includes("Yearly")) {
        setPlanDuration("yearly");
      } else if (currentPlanId.includes("Monthly")) {
        setPlanDuration("monthly");
      }

      // Set attendee values based on current plan
      if (currentPlanId.includes("Live-Webinars")) {
        // Find matching live plan attendee value
        const matchingLivePlan = LIVE_PRICES.find(
          (plan) => plan.value === currentAttendees.toString(),
        );
        if (matchingLivePlan) {
          setLiveValue(matchingLivePlan.value);
        }
      } else if (currentPlanId.includes("Automated-Gold")) {
        // Find matching automated plan attendee value
        const matchingAutomatedPlan = AUTOMATED_PRICES.find(
          (plan) => plan.value === currentAttendees.toString(),
        );
        if (matchingAutomatedPlan) {
          setAutomatedValue(matchingAutomatedPlan.value);
        }
      } else if (currentPlanId.includes("All-In-One")) {
        // Find matching live+automated plan attendee value
        const matchingLiveAutoPlan = LIVE_AUTO_PRICES.find(
          (plan) => plan.value === currentAttendees.toString(),
        );
        if (matchingLiveAutoPlan) {
          setLiveAutomatedValue(matchingLiveAutoPlan.value);
        }
      }
    }
  }, [currentPlanId, currentAttendees]);

useEffect(() => {
  const token = decodeMetadataToken();
  const planId = token?.companies?.[0]?.subscription?.planId;
  const isGHLEvent = (planId?.toLowerCase())?.includes('agency') || (planId?.toLowerCase())?.includes('creators');
  setIsGHLEvent(isGHLEvent);
}, [])
console.log("isGHLEvent==>",isGHLEvent);



  // Handlers
  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlanDuration(e.target.checked ? "yearly" : "monthly");
  };

  const getStartForFree = (
    planType: "free" | "live" | "automated" | "liveAuto",
  ) => {
    let plan, formattedPlanType, attendees;

    if (planType === "live") {
      plan =
        LIVE_PRICES.find((opt) => opt.value === liveValue) || LIVE_PRICES[0];
      formattedPlanType = "Live-Webinars";
      attendees = plan.value; // e.g., "50 Attendees" -> "50"
    } else if (planType === "automated") {
      plan =
        AUTOMATED_PRICES.find((opt) => opt.value === automatedValue) ||
        AUTOMATED_PRICES[0];
      formattedPlanType = "Automated-Gold";
      attendees = plan.value; // e.g., "200 Attendees" -> "200"
    } else if (planType === "liveAuto") {
      plan =
        LIVE_AUTO_PRICES.find((opt) => opt.value === liveAutomatedValue) ||
        LIVE_AUTO_PRICES[0];
      formattedPlanType = "All-In-One";
      attendees = plan.value; // e.g., "50 Live & 200 Automated" -> "50-200"
    } else if (planType === "free") {
      formattedPlanType = "free-trial-plan";
      onPlanSelect(formattedPlanType);
      return;
    }

    const currency = "USD";
    const duration =
      planDuration.charAt(0).toUpperCase() + planDuration.slice(1); // "Monthly" or "Yearly"

    const planId = `${formattedPlanType}-${currency}-${duration}-${attendees}`;
    onPlanSelect(planId);
  };

  const contactSales = () => {
    window.location.href = `${process.env.REACT_APP_EASYWEBINAR_URL}/demo/`;
  };

  // In Live Webinars card
  const liveSelected =
    LIVE_PRICES.find((opt) => opt.value === liveValue) || LIVE_PRICES[0];
  // In Automated Webinars card
  const automatedSelected =
    AUTOMATED_PRICES.find((opt) => opt.value === automatedValue) ||
    AUTOMATED_PRICES[0];
  // In Live + Automated card
  const liveAutoSelected =
    LIVE_AUTO_PRICES.find((opt) => opt.value === liveAutomatedValue) ||
    LIVE_AUTO_PRICES[0];

  const eventPlanKeys = ["50", "200", "500"] as const;
  const funnelPlanKeys = ["3000", "6000", "10000"] as const;

  const planCardsData = useMemo(() => {
    return categoryTab === 0
      ? eventPlanKeys.map((k) => ({
          key: k,
          plan: isGHLEvent ? PRICING_GHLEvent.events[k] : ALL_IN_ONE_PRICES.events[k],
        }))
      : funnelPlanKeys.map((k) => ({
          key: k,
          plan: isGHLEvent ? PRICING_GHLEvent.funnels[k] : ALL_IN_ONE_PRICES.funnels[k],
        }));
  }, [categoryTab, isGHLEvent]);

  const getPlanAddons = (addonIds: string[] | undefined) =>
    PricingAddons?.filter((a) => addonIds?.includes(a.id)) ?? [];

  const toggleAddon = (planKey: string, addonId: string) => {
    setSelectedAddonsByPlan((prev) => {
      const current = prev[planKey] ?? [];
      const next = current.includes(addonId)
        ? current.filter((id) => id !== addonId)
        : [...current, addonId];
      return { ...prev, [planKey]: next };
    });
  };

  const getDisplayPrice = (
    planKey: string,
    plan: { monthly: number; annualPlanPerMonth: number },
    addonIds: string[] | undefined,
    durationOverride?: "monthly" | "yearly",
  ) => {
    if(isGHLEvent) return (plan.monthly * 0.35).toFixed(2);
    const dur = durationOverride ?? planDuration;
    const base = dur === "yearly" ? plan.annualPlanPerMonth : plan.monthly;
    if (dur === "yearly" || !addonIds?.length) return base;
    const selected = selectedAddonsByPlan[planKey] ?? [];
    const addonTotal = getPlanAddons(addonIds)
      .filter((a) => selected.includes(a.id))
      .reduce((s, a) => s + a.price, 0);
    return base + addonTotal;
  };

  const selectPlan = (plan: any, isYearly: boolean) => {
    onPlanSelect(isYearly ? plan.YearlyPlanID : plan.MonthlyPlanID);
  };

  const getPlanDescription = (planKey: string) => {
    if (categoryTab === 0) {
      const d: Record<string, string> = {
        "50": "Best for small live webinars",
        "200": "Best for live, simulive & evergreen webinars",
        "500": "Best for high-volume live & automated webinars",
      };
      return d[planKey] ?? "";
    }
    const d: Record<string, string> = {
      "3000": "Best for getting started with evergreen",
      "6000": "Best for scaling automated webinars",
      "10000": "Best for high-volume evergreen",
    };
    return d[planKey] ?? "";
  };

  const getSaveAmount = (plan: {
    savingsAmount?: number;
    annualMaxPrice?: number;
    annualPlanPerMonth?: number;
  }) =>
    plan.savingsAmount ??
    (plan.annualMaxPrice != null && plan.annualPlanPerMonth != null
      ? (plan.annualMaxPrice - plan.annualPlanPerMonth) * 12
      : 0);

  const PlanCard = ({
    planKey,
    plan,
    isMostPopular,
    currentAttendees,
  }: {
    planKey: string;
    plan: IPlan;
    isMostPopular?: boolean;
    currentAttendees: number | string;
  }) => {
    console.log("planKey==>", planKey);

    const cardDuration = durationByPlanKey[planKey] ?? planDuration;
    const setCardDuration = (d: "monthly" | "yearly") =>
      setDurationByPlanKey((prev) => ({ ...prev, [planKey]: d }));
    const isYearly = cardDuration === "yearly";
    console.log("currentPlanId == planKey", currentPlanId, planKey);

    return (
      <Grid
        key={planKey}
        size={{ xs: 12, md: 6, lg: !hideTabs ? 3 : 4 }}
        maxWidth={{ xs: "350px", md: "unset" }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: "12px",
            minHeight: 420,
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: isMostPopular
              ? (theme.palette.primary?.main ?? "#178EFC")
              : "#E8EAF0",
            background: "#FFF",
            boxShadow: isMostPopular
              ? "0px 4px 20px rgba(23, 142, 252, 0.15)"
              : "0px 4px 12px 0px rgba(43, 43, 48, 0.06)",
            position: "relative",
            mt: isMostPopular ? "-35px" : 0,
            height: isMostPopular ? "calc(100% + 35px)" : "100%",
          }}
        >
          {isMostPopular && (
            <Box
              sx={{
                background: theme.palette.primary?.main ?? "#178EFC",
                color: "#fff",
                py: 1,
                textAlign: "center",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              Most Popular
            </Box>
          )}
          <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
            {currentPlanId?.includes(planKey) &&
              subscriptionStatus === "in_trial" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: isMostPopular ? 40 : 0,
                    right: 0,
                    background: "#22B060",
                    color: "#fff",
                    p: "4px 8px 4px 12px",
                    borderRadius: "0 8px 0 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    zIndex: 2,
                  }}
                >
                  Currently in trial
                </Box>
              )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                mb: 1.5,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  mb={0.25}
                  align="left"
                >
                  {plan.planName}
                </Typography>
                <Typography
                  variant="body2"
                  color="#666"
                  align="left"
                  minHeight={50}
                  sx={{ fontSize: "13px" }}
                >
                  {getPlanDescription(planKey) ||
                    (plan.liveAttendees
                      ? `${plan.liveAttendees} Live${plan.automatedAttendees ? ` + ${plan.automatedAttendees?.toLocaleString()} Automated` : ""} Attendees`
                      : `${(plan.automatedAttendees ?? plan.attendees)?.toLocaleString()} Attendees`)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#666666",
                      textAlign: "right",
                    }}
                  >
                    Annual
                    {!isGHLEvent && getSaveAmount(plan) > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          mt: 0.25,
                          display: "block",
                          lineHeight: 1,
                          fontWeight: 500,
                        }}
                      >
                        Save ${getSaveAmount(plan).toLocaleString()}
                      </Typography>
                    )}
                  </Typography>
                  <Switch
                    checked={isYearly}
                    onChange={(_, checked) =>
                      setCardDuration(checked ? "yearly" : "monthly")
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: theme.palette.primary?.main ?? "#178EFC",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor:
                            theme.palette.primary?.main ?? "#178EFC",
                        },
                    }}
                  />
                </Box>
              </Box>
            </Box>
            {categoryTab !== 1 &&
              (planKey === "50" || planKey === "200" || planKey === "500") && (
                <Typography
                  variant="body2"
                  color="#000"
                  sx={{ fontSize: "15px", mb: 3, mt: 2, fontWeight: 500 }}
                >
                  {planKey === "50" && "50 Live Attendees"}
                  {planKey === "200" && "200 Live + 1,000 Automated Attendees"}
                  {planKey === "500" && "500 Live + 2,500 Automated Attendees"}
                </Typography>
              )}
            {categoryTab === 1 &&
              (planKey === "3000" ||
                planKey === "6000" ||
                planKey === "10000") && (
                <Typography
                  variant="body2"
                  color="#000"
                  sx={{ fontSize: "15px", mb: 3, mt: 2, fontWeight: 500 }}
                >
                  {planKey === "3000" && "3,000 Automated Attendees"}
                  {planKey === "6000" && "6,000 Automated Attendees"}
                  {planKey === "10000" && "10,000 Automated Attendees"}
                </Typography>
              )}
            {isYearly && !isGHLEvent && (
              <Typography
                component="span"
                fontSize={20}
                color="rgba(0, 0, 0, 0.314)"
                fontWeight={500}
                mr={1}
                sx={{ textDecoration: "line-through" }}
              >
                ${plan.annualMaxPrice}
              </Typography>
            )}
            {isYearly && isGHLEvent && (
              <Typography
                component="span"
                fontSize={20}
                color="rgba(0, 0, 0, 0.314)"
                fontWeight={500}
                mr={1}
                sx={{ textDecoration: "line-through" }}
              >
                ${plan.monthly}
              </Typography>
            )}
            <Typography
              variant="h5"
              fontWeight={700}
              color="#222"
              display="flex"
              alignItems="baseline"
              mb={0.5}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    fontSize: "2rem",
                  }}
                >
                  $
                  <Typography
                    component="span"
                    fontWeight="inherit"
                    fontSize={34}
                  >
                    {getDisplayPrice(planKey, plan, plan.addons, cardDuration)}
                  </Typography>
                </span>
                <Typography
                  component="span"
                  fontSize={16}
                  color="#666666"
                  fontWeight={400}
                >
                  /month
                </Typography>
              </Box>
            </Typography>
            {!isGHLEvent && isYearly && categoryTab === 1 && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ fontSize: "12px", mb: 1.5 }}
              >
                Billed Annually — You're saving $
                {getSaveAmount(plan).toLocaleString()} /yr
              </Typography>
            )}
            {!isGHLEvent && isYearly && categoryTab !== 1 && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ fontSize: "12px", mb: 1.5 }}
              >
                Billed Annually — You're saving $
                {getSaveAmount(plan).toLocaleString()} /yr
              </Typography>
            )}
            {isGHLEvent && isYearly && (
                <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ fontSize: "12px", mb: 1.5 }}
              >
                Billed Annually
              </Typography>
            )}

            {!isGHLEvent && !isYearly && categoryTab === 1 && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ fontSize: "12px", mb: 1.5 }}
              >
                Switch to annual billing — pay for 10 months + free add-ons.
              </Typography>
            )}
            {!isGHLEvent && !isYearly && categoryTab !== 1 && planKey === "50" && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ fontSize: "12px", mb: 1.5 }}
              >
                Upgrade to annual - pay for 10 months only
              </Typography>
            )}
            {!isGHLEvent && !isYearly &&
              categoryTab !== 1 &&
              (planKey === "200" || planKey === "500") && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ fontSize: "12px", mb: 1.5 }}
                >
                  Switch to annual billing — pay for 10 months + free add-ons.
                </Typography>
              )}
            {plan.addons?.length ? (
              <Box
                sx={{ bgcolor: "#f5f5f5", borderRadius: "8px", p: 1.5, mb: 2 }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="#333"
                  mb={1}
                  fontSize="12px"
                >
                  {isYearly
                    ? "Included free with annual (normally paid add-ons)"
                    : "Add-ons (optional)"}
                </Typography>
                {getPlanAddons(plan.addons).map((addon) => {
                  const checked =
                    isYearly ||
                    (selectedAddonsByPlan[planKey] ?? []).includes(addon.id);
                  return (
                    <Box
                      key={addon.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                      onClick={() =>
                        !isYearly && toggleAddon(planKey, addon.id)
                      }
                    >
                      <Box
                        component="input"
                        type="checkbox"
                        checked={checked}
                        // disabled={isYearly}
                        // onChange={() => !isYearly && toggleAddon(planKey, addon.id)}
                        sx={{
                          width: 18,
                          height: 18,
                          mt: 0.25,
                          accentColor: theme.palette.primary?.main ?? "#178EFC",
                          cursor: isYearly ? "default" : "pointer",
                          borderRadius: "8px",
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="#666"
                        fontSize="12px"
                        component="label"
                        sx={{ cursor: isYearly ? "default" : "pointer" }}
                      >
                        {addon.title} — {addon.description}
                        {isYearly ? (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              color: "#999",
                              textDecoration: "line-through",
                            }}
                          >
                            ${addon.price}/mo
                          </Box>
                        ) : (
                          <Box
                            component="span"
                            sx={{ ml: 0.5, fontWeight: 600 }}
                          >
                            ${addon.price}/mo
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : null}
             <Button
                variant="contained"
                size="large"
                onClick={() => selectPlan(plan, isYearly)}
                sx={{
                  width: "100%",
                  mb: 1,
                  bgcolor: "#2B2B30",
                  color: "#fff",
                  "&:hover": { bgcolor: "#1a1a1a" },
                }}
              >
               Buy now
              </Button>
            <Box sx={{ mt: 2 }}>
              {planFeaturesList(isYearly, isGHLEvent)?.[planKey]?.map(
                (section: any, sectionIndex: any) => (
                  <Box
                    key={sectionIndex}
                    sx={{ mb: sectionIndex === 0 ? 2 : 0 }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#222"
                      mb={1.5}
                    >
                      {section.title}
                    </Typography>
                    <Box>
                      {section.list.map((feature: any, featureIndex: any) => (
                        <Box key={featureIndex} sx={{ mb: 0.85 }}>
                          <Typography
                            variant="body2"
                            color="#4a4a4a"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontSize: 14,
                            }}
                          >
                            <Box
                              sx={{
                                color: theme.palette.primary?.main,
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              ✓
                            </Box>{" "}
                            <span>{feature.label}</span>
                            {!!feature?.tooltip && (
                              <Tooltip arrow title={feature.tooltip || ""}>
                                <InfoIcon
                                  sx={{
                                    fontSize: 14,
                                    color: "#999",
                                    cursor: "help",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Typography>
                          {feature?.tag ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                mt: 0.25,
                                ml: 2.5,
                                px: 1,
                                py: 0.25,
                                bgcolor: "#d4edda",
                                color: "#155724",
                                fontSize: 12,
                                fontWeight: 600,
                                borderRadius: 10,
                                textTransform: "lowercase",
                              }}
                            >
                              {feature.tag}
                            </Box>
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ),
              )}
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  // Memoized FEATURES array
  const FEATURES = useMemo(
    () => [
      // Session Duration & Capacity
      { section: "Session Duration & Capacity" },
      {
        label: "2 Hour Session Duration (Live)",
        plans: [true, false, false, false],
        info: "Maximum 2 hours for live webinar sessions.",
      },
      {
        label: "4 Hour Session Duration (Live)",
        plans: [false, true, false, true],
        info: "Maximum 4 hours for live webinar sessions.",
      },
      {
        label: "Unlimited Session Duration (Automated)",
        plans: [false, true, true, true],
        info: "No time limit for automated webinar sessions.",
      },
      {
        label: "1 Host",
        plans: [true, true, true, true],
        info: "One main presenter for the webinar.",
      },
      {
        label: "1 Co-Host/Moderator",
        plans: [true, false, false, false],
        info: "One additional co-host or moderator.",
      },
      {
        label: "3 Co-Hosts/Moderators",
        plans: [false, true, true, true],
        info: "Up to three additional co-hosts or moderators.",
      },

      // Live & Automated
      { section: "Live & Automated" },
      {
        label: "EasyCast for YouTube Live streaming",
        plans: [false, false, false, true],
        info: "Stream directly to YouTube Live.",
      },
      {
        label: "Live Chat with Automated sessions",
        plans: [false, true, true, true],
        info: "Enable live chat for automated webinars.",
      },
      {
        label: "Reporting & Analytics",
        plans: [true, true, true, true],
        info: "Comprehensive analytics and reporting for your webinars.",
      },
      {
        label: "Custom landing pages + embed forms",
        plans: [true, true, true, true],
        info: "Create custom landing pages and embed registration forms.",
      },
      {
        label: "Unlimited Live Webinars",
        plans: [false, "Up to 4 Live Webinars", true, true],
        info: "Host unlimited live webinars (limits may apply to some plans).",
      },
      {
        label: "Green Room Access",
        plans: [true, false, true, true],
        info: "Private pre-webinar chat for presenters.",
      },
      {
        label: "Automated Webinars",
        plans: [false, true, true, true],
        info: "Run fully automated webinars.",
      },
      {
        label: "Evergreen Webinars",
        plans: [false, true, true, true],
        info: "Fully automated, on-demand webinars.",
      },
      {
        label: "Timed polls, CTAs (Automated)",
        plans: [false, true, true, true],
        info: "Schedule polls and CTAs in automated webinars.",
      },
      {
        label: "Dynamic Presentation Control",
        plans: [true, true, true, true],
        info: "Control presentations dynamically during webinars.",
      },
      {
        label: "Real-time offers, polls, Q&A",
        plans: [true, true, true, true],
        info: "Engage attendees with real-time offers, polls, and Q&A.",
      },
      {
        label: "Instant replay recordings",
        plans: [true, true, true, true],
        info: "Access instant replays of your webinars.",
      },
      {
        label: "Simulated Chat (Automated)",
        plans: [false, true, true, true],
        info: "Pre-scripted chat shown in simulive webinars.",
      },
      {
        label: "Attendee Spotlight",
        plans: [true, true, true, true],
        info: "Highlight attendees during the webinar.",
      },
      {
        label: "One-Click Registration",
        plans: [true, true, true, true],
        info: "Let users register with a single click.",
      },
      {
        label: "Built-in social sharing",
        plans: [true, true, true, true],
        info: "Let users share your webinar on social media.",
      },
      {
        label: "Calendar integration",
        plans: [true, true, true, true],
        info: "Integrate webinars with calendar apps.",
      },
      {
        label: "Smart engagement + conversion tracking",
        plans: [true, true, true, true],
        info: "Track attendee engagement and conversions.",
      },
      {
        label: "Email campaigns with intelligent targeting",
        plans: [true, true, true, true],
        info: "Send targeted email campaigns to your audience.",
      },
      {
        label: "API Access",
        plans: [false, false, false, true],
        info: "Access platform features via API.",
      },

      // Storage and Add-on Accounts
      { section: "Storage and Add-on Accounts" },
      { label: "2 GB Storage Space", plans: [false, true, true, true] },
      { label: "4 GB Storage Space", plans: [false, false, true, true] },
      { label: "Unlimited Storage Space", plans: [false, false, false, true] },
      { label: "Unlimited Team Members", plans: [false, true, true, true] },

      // RM & Marketing tools Integrations
      { section: "CRM & Marketing tools Integrations" },
      { label: "Zapier Integration", plans: [true, true, true, true] },
      { label: "Mailchimp", plans: [true, true, true, true] },
      { label: "Aweber", plans: [true, true, true, true] },
      { label: "I-contact", plans: [true, true, true, true] },
      { label: "Keap", plans: [true, true, true, true] },
      { label: "GetResponse", plans: [true, true, true, true] },
      { label: "Constant Contact", plans: [true, true, true, true] },
      { label: "Go-High Level", plans: [true, true, true, true] },
      { label: "Hubspot", plans: [true, true, true, true] },
      { label: "Salesforce Pardot", plans: [false, false, false, true] },
      { label: "Active Campaign", plans: [true, true, true, true] },
      { label: "Kit", plans: [true, true, true, true] },
      { label: "Drip", plans: [true, true, true, true] },
      { label: "Sendgrid", plans: [true, true, true, true] },
      { label: "Ontraport", plans: [true, true, true, true] },

      // Support
      { section: "Support" },
      { label: "Standard Support", plans: [false, true, true, true] },
      {
        label: "Monthly 1:1 Strategy Call with CSM",
        plans: [false, false, true, true],
      },
      {
        label: "Dedicated Account Manager",
        plans: [false, false, false, true],
      },
      { label: "VIP Support & SLA", plans: [false, false, false, true] },
      { label: "Live Session Monitoring", plans: [false, false, false, true] },

      // Security
      { section: "Security" },
      { label: "GDPR Compliance", plans: [true, true, true, true] },
      { label: "SSO Login", plans: [true, true, true, true] },
      { label: "Presenter control panel", plans: [true, true, true, true] },
      {
        label: "Two-Factor Authentication (2FA)",
        plans: [true, true, true, true],
      },

      // Custom Services
      { section: "Custom Services" },
      { label: "White Labeling", plans: [false, "add-on", "add-on", true] },
      { label: "Custom Domain", plans: [false, "add-on", "add-on", true] },
      {
        label: "Funnel Audit & Strategy Sessions",
        plans: [false, true, true, true],
      },
      {
        label: "Priority Access to Beta Releases",
        plans: [false, false, false, true],
      },
    ],
    [liveSelected, automatedSelected, liveAutoSelected],
  );

  // Helper function to check if this is the current plan
  const isCurrentPlan = (
    planType: "free" | "live" | "automated" | "liveAuto",
  ) => {
    if (!currentPlanId) return false;

    if (planType === "free") {
      // For free plan, only check the plan ID
      return currentPlanId.includes("free-trial-plan");
    }

    // Check if the current plan ID contains the plan type
    let planTypeMatch = false;
    let attendeeMatch = false;
    let billingFrequencyMatch = false;

    // Check billing frequency match
    const currentBillingFrequency =
      planDuration === "yearly" ? "Yearly" : "Monthly";
    billingFrequencyMatch = currentPlanId.includes(currentBillingFrequency);

    if (planType === "live") {
      planTypeMatch = currentPlanId.includes("Live-Webinars");
      attendeeMatch = currentAttendees === parseInt(liveValue);
    } else if (planType === "automated") {
      planTypeMatch = currentPlanId.includes("Automated-Gold");
      attendeeMatch = currentAttendees === parseInt(automatedValue);
    } else if (planType === "liveAuto") {
      planTypeMatch = currentPlanId.includes("All-In-One");
      attendeeMatch = currentAttendees === parseInt(liveAutomatedValue);
    }

    return planTypeMatch && attendeeMatch && billingFrequencyMatch;
  };

  // Helper function to check if this is the current trial plan
  // const isCurrentTrialPlan = (
  //   planType: "free" | "live" | "automated" | "liveAuto"
  // ) => {
  //   // Check if subscription is in trial status
  //   if (subscriptionStatus !== "in_trial" || !currentPlanId) return false;

  //   if (planType === "free") {
  //     return currentPlanId.includes("free-trial-plan");
  //   }

  //   // For trial plans, check if the current plan matches the plan type, attendee count, and billing frequency
  //   let planTypeMatch = false;
  //   let attendeeMatch = false;
  //   let billingFrequencyMatch = false;

  //   // Check billing frequency match
  //   const currentBillingFrequency =
  //     planDuration === "yearly" ? "Yearly" : "Monthly";
  //   billingFrequencyMatch = currentPlanId.includes(currentBillingFrequency);

  //   if (planType === "live") {
  //     planTypeMatch = currentPlanId.includes("Live-Webinars");
  //     attendeeMatch = currentAttendees === parseInt(liveValue);
  //   } else if (planType === "automated") {
  //     planTypeMatch = currentPlanId.includes("Automated-Gold");
  //     attendeeMatch = currentAttendees === parseInt(automatedValue);
  //   } else if (planType === "liveAuto") {
  //     planTypeMatch = currentPlanId.includes("All-In-One");
  //     attendeeMatch = currentAttendees === parseInt(liveAutomatedValue);
  //   }

  //   return planTypeMatch && attendeeMatch && billingFrequencyMatch;
  // };

  // Render main component
  return (
    <>
      <Box className="v3-pricing-plan-table" sx={{ mb: 4 }} id={id}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="16px"
          sx={{ mb: 3 }}
        >
          {!hideTabs && (
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              centered
            >
              <Tab
                label="Plans"
                sx={{
                  textTransform: "capitalize",
                  fontSize: { xs: "14px", sm: "16px" },
                  padding: { xs: "6px 12px" },
                }}
              />
              <Tab
                label="Compare Plans"
                sx={{
                  textTransform: "capitalize",
                  fontSize: { xs: "14px", sm: "16px" },
                  padding: { xs: "6px 12px" },
                }}
              />
            </Tabs>
          )}

          {tabValue === 1 ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap="16px"
              ml="auto"
              mb={2}
            >
              {saleLive ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    background: "#F5F5F5",
                    borderRadius: "100px",
                    padding: "4px",
                    border: "1px solid #E0E0E0",
                    position: "relative",
                    // width: "fit-content",
                    minWidth: "260px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      zIndex: 2,
                      cursor: "pointer",
                      py: 1.5,
                      px: 2,
                      backgroundColor:
                        planDuration === "monthly" ? "#000000" : "transparent",
                      borderRadius: "100px",
                      transition: "background-color 0.3s ease",
                    }}
                    onClick={() => setPlanDuration("monthly")}
                  >
                    <Typography
                      sx={{
                        color:
                          planDuration === "monthly" ? "#FFFFFF" : "#000000",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: 1.2,
                      }}
                    >
                      Monthly - 40% OFF (first 3 months)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      zIndex: 2,
                      cursor: "pointer",
                      py: 1.5,
                      px: 2,
                      backgroundColor:
                        planDuration === "yearly" ? "#000000" : "transparent",
                      borderRadius: "100px",
                      transition: "background-color 0.3s ease",
                    }}
                    onClick={() => setPlanDuration("yearly")}
                  >
                    <Typography
                      sx={{
                        color:
                          planDuration === "yearly" ? "#FFFFFF" : "#000000",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: 1.2,
                      }}
                    >
                      Yearly - 50% OFF
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <FormGroup>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Typography
                      color={planDuration === "monthly" ? "#22B060" : "#000000"}
                      fontWeight="500"
                    >
                      Monthly
                    </Typography>
                    <AntSwitch
                      checked={planDuration === "yearly"}
                      onChange={handleDurationChange}
                      inputProps={{ "aria-label": "plan duration toggle" }}
                    />
                    <Typography
                      display="flex"
                      color={planDuration === "yearly" ? "#22B060" : "#000000"}
                      alignItems="center"
                      gap={1}
                      fontWeight="500"
                    >
                      Yearly{" "}
                      <Typography
                        component="span"
                        color="#2B2B30"
                        fontSize={{ xs: "12px", sm: "13px" }}
                        fontWeight={500}
                      >
                        (2 Months Free)
                      </Typography>
                    </Typography>
                  </Stack>
                </FormGroup>
              )}
            </Box>
          ) : null}
        </Box>
        {tabValue === 0 ? (
          <Box className="ew-plan-boxes-con" mt={2}>
            <Box
              sx={{
                display: "inline-flex",
                border: "1px solid #d1d5db",
                borderRadius: "9999px",
                p: "4px",
                bgcolor: "transparent",
                mb: 4,
              }}
            >
              <Tooltip
                title="Events support both live and evergreen webinars — ideal for launches, engagement, and full-funnel experiences."
                placement="top"
                arrow
              >
                <Button
                  onClick={() => setCategoryTab(0)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    borderRadius: "9999px",
                    px: 2.5,
                    py: 1,
                    minWidth: 0,
                    bgcolor: categoryTab === 0 ? "#ede9f7" : "transparent",
                    border:
                      categoryTab === 0
                        ? "2px solid #2563eb"
                        : "2px solid transparent",
                    color: "#374151",
                    "&:hover": {
                      bgcolor:
                        categoryTab === 0 ? "#e5dff5" : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  Live + Automated
                </Button>
              </Tooltip>
              <Tooltip
                title="Funnels are evergreen-only webinar experiences optimized for higher lead volume at a lower cost."
                placement="top"
                arrow
              >
                <Button
                  onClick={() => setCategoryTab(1)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    borderRadius: "9999px",
                    px: 2.5,
                    py: 1,
                    minWidth: 0,
                    bgcolor: categoryTab === 1 ? "#ede9f7" : "transparent",
                    border:
                      categoryTab === 1
                        ? "2px solid #2563eb"
                        : "2px solid transparent",
                    color: "#374151",
                    "&:hover": {
                      bgcolor:
                        categoryTab === 1 ? "#e5dff5" : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  Automated Only
                </Button>
              </Tooltip>
            </Box>
            <Grid
              container
              spacing={2}
              width="100%"
              justifyContent={{ xs: "center", md: "unset" }}
              pt={5}
              sx={{ "--Grid-parent-columns": 9 } as any}
            >
              {planCardsData.map(({ key: planKey, plan }, index) => (
                <PlanCard
                  key={planKey}
                  planKey={planKey}
                  plan={plan}
                  isMostPopular={index === 1}
                  currentAttendees={Number(currentAttendees)}
                />
              ))}
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              background: "#fff",
              borderRadius: 3,
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
            }}
            overflow="visible"
            position="relative"
          >
            <Box
              position="sticky"
              top={0}
              zIndex={1}
              display="flex"
              width="100%"
              minWidth={1100}
              border="1px solid #E8EAF0"
              borderRadius="12px"
              bgcolor="#ffffff"
            >
              {/* Info column */}
              <Box
                sx={{
                  minWidth: 240,
                  background:
                    "linear-gradient(135deg, #4F8CFF 0%, #6DC7FF 100%)",
                  color: "#fff",
                  borderTopLeftRadius: 12,
                  borderBottomLeftRadius: 12,
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h5" fontWeight={700} mb={1}>
                  Compare plans
                </Typography>
              </Box>
              {/* Plan columns */}
              {[
                {
                  name: "Launch",
                  color: theme.palette.primary.main,
                  price: saleLive
                    ? planDuration === "yearly"
                      ? Math.round((liveSelected.yearlySalePrice ?? 0) * 0.5)
                      : Math.round((liveSelected.monthly ?? 0) * 0.6)
                    : planDuration === "yearly"
                      ? liveSelected.annual
                      : liveSelected.monthly,
                  priceLabel: planDuration === "yearly" ? "/month" : "/month",
                  dropdown: (
                    <Select
                      sx={{ width: "90%", mb: 1, mt: 1, background: "#fff" }}
                      size="small"
                      value={liveValue}
                      onChange={(e) => setLiveValue(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& ul": { p: "4px" },
                            "& li": { p: "8px 16px", borderRadius: "4px" },
                          },
                        },
                      }}
                    >
                      {LIVE_PRICES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        pointerEvents: isCurrentPlan("live") ? "none" : "auto",
                        bgcolor: isCurrentPlan("live")
                          ? "rgba(79, 140, 255, 0.1)"
                          : "#2B2B30",
                        color: isCurrentPlan("live") ? "#2B2B30" : "#fff",
                        border: isCurrentPlan("live")
                          ? "1px solid #4F8CFF"
                          : undefined,
                        boxShadow: isCurrentPlan("live") ? "none" : undefined,
                        "&:hover": {
                          bgcolor: isCurrentPlan("live")
                            ? "rgba(79, 140, 255, 0.2)"
                            : "#2B2B30",
                          boxShadow: isCurrentPlan("live") ? "none" : undefined,
                        },
                      }}
                      onClick={() => getStartForFree("live")}
                    >
                      {isCurrentPlan("live") ? "Current Plan" : "Upgrade"}
                    </Button>
                  ),
                },
                {
                  name: "Scale",
                  color: theme.palette.primary.main,
                  price: saleLive
                    ? planDuration === "yearly"
                      ? Math.round(
                          (automatedSelected.yearlySalePrice ?? 0) * 0.5,
                        )
                      : Math.round((automatedSelected.monthly ?? 0) * 0.6)
                    : planDuration === "yearly"
                      ? automatedSelected.annual
                      : automatedSelected.monthly,
                  priceLabel: planDuration === "yearly" ? "/month" : "/month",
                  dropdown: (
                    <Select
                      sx={{ width: "90%", mb: 1, mt: 1, background: "#fff" }}
                      size="small"
                      value={automatedValue}
                      onChange={(e) => setAutomatedValue(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& ul": { p: "4px" },
                            "& li": { p: "8px 16px", borderRadius: "4px" },
                          },
                        },
                      }}
                    >
                      {AUTOMATED_PRICES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        pointerEvents: isCurrentPlan("automated")
                          ? "none"
                          : "auto",
                        bgcolor: isCurrentPlan("automated")
                          ? "rgba(79, 140, 255, 0.1)"
                          : "#2B2B30",
                        color: isCurrentPlan("automated") ? "#2B2B30" : "#fff",
                        border: isCurrentPlan("automated")
                          ? "1px solid #4F8CFF"
                          : undefined,
                        boxShadow: isCurrentPlan("automated")
                          ? "none"
                          : undefined,
                        "&:hover": {
                          bgcolor: isCurrentPlan("automated")
                            ? "rgba(79, 140, 255, 0.2)"
                            : "#2B2B30",
                          boxShadow: isCurrentPlan("automated")
                            ? "none"
                            : undefined,
                        },
                      }}
                      onClick={() => getStartForFree("automated")}
                    >
                      {isCurrentPlan("automated") ? "Current Plan" : "Upgrade"}
                    </Button>
                  ),
                },
                {
                  name: "Professional",
                  color: theme.palette.primary.main,
                  price: liveAutoSelected.custom
                    ? "custom"
                    : saleLive
                      ? planDuration === "yearly"
                        ? Math.round(
                            (liveAutoSelected.yearlySalePrice ?? 0) * 0.5,
                          )
                        : Math.round((liveAutoSelected.monthly ?? 0) * 0.6)
                      : planDuration === "yearly"
                        ? liveAutoSelected.annual
                        : liveAutoSelected.monthly,
                  priceLabel: planDuration === "yearly" ? "/month" : "/month",
                  dropdown: (
                    <Select
                      sx={{ width: "90%", mb: 1, mt: 1, background: "#fff" }}
                      size="small"
                      value={liveAutomatedValue}
                      onChange={(e) => setLiveAutomatedValue(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& ul": { p: "4px" },
                            "& li": { p: "8px 16px", borderRadius: "4px" },
                          },
                        },
                      }}
                    >
                      {LIVE_AUTO_PRICES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        pointerEvents: liveAutoSelected.custom
                          ? "auto"
                          : isCurrentPlan("liveAuto")
                            ? "none"
                            : "auto",
                        bgcolor:
                          !liveAutoSelected.custom && isCurrentPlan("liveAuto")
                            ? "rgba(79, 140, 255, 0.1)"
                            : "#2B2B30",
                        color:
                          !liveAutoSelected.custom && isCurrentPlan("liveAuto")
                            ? "#2B2B30"
                            : "#fff",
                        border:
                          !liveAutoSelected.custom && isCurrentPlan("liveAuto")
                            ? "1px solid #4F8CFF"
                            : undefined,
                        boxShadow:
                          !liveAutoSelected.custom && isCurrentPlan("liveAuto")
                            ? "none"
                            : undefined,
                        "&:hover": {
                          bgcolor:
                            !liveAutoSelected.custom &&
                            isCurrentPlan("liveAuto")
                              ? "rgba(79, 140, 255, 0.2)"
                              : "#2B2B30",
                          boxShadow:
                            !liveAutoSelected.custom &&
                            isCurrentPlan("liveAuto")
                              ? "none"
                              : undefined,
                        },
                      }}
                      onClick={() =>
                        liveAutoSelected.custom
                          ? contactSales()
                          : getStartForFree("liveAuto")
                      }
                    >
                      {liveAutoSelected.custom
                        ? "Book a call"
                        : isCurrentPlan("liveAuto")
                          ? "Current Plan"
                          : "Upgrade"}
                    </Button>
                  ),
                },
                {
                  name: "Enterprise",
                  color: theme.palette.primary.main,
                  price: null,
                  priceLabel: "",
                  dropdown: (
                    <Typography
                      fontWeight={500}
                      mt="13px"
                      mb={1}
                      fontSize={14}
                      textAlign="center"
                    >
                      Up to <br />
                      <span
                        style={{
                          fontSize: 24,
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        500K
                      </span>
                      <br />
                      attendees
                    </Typography>
                  ),
                  button: (
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      sx={{
                        mt: 1,
                        bgcolor: "#2B2B30",
                        color: "#fff",
                        border: "1px solid #2B2B30",
                        "&:hover": {
                          bgcolor: "#2B2B30",
                          color: "#fff",
                          border: "1px solid #2B2B30",
                        },
                      }}
                      onClick={() => contactSales()}
                    >
                      Upgrade
                    </Button>
                  ),
                },
              ]
                .filter((plan) => plan.name !== "Enterprise")
                .map((plan, idx, arr) => (
                  <Box
                    key={plan.name}
                    position="relative"
                    sx={{
                      flex: 1,
                      minWidth: 220,
                      borderRight:
                        idx < arr.length - 1 ? "1px solid #E8EAF0" : "none",
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    {plan.name === "Live + Automated" && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -24,
                          left: 0,
                          width: "100%",
                          height: 24,
                          background: theme.palette.primary.main,
                          borderTopLeftRadius: "12px",
                          borderTopRightRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 2,
                        }}
                      >
                        <Typography
                          sx={{ color: "#fff", fontWeight: 500, fontSize: 14 }}
                        >
                          Recommended
                        </Typography>
                      </Box>
                    )}
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={plan.color}
                      mb={1}
                    >
                      {plan.name}
                    </Typography>
                    {/* {plan.price === "custom" && <Typography variant="h5" fontWeight={700} fontSize="16px" mb='20px' color={theme.palette.primary.main} textAlign="center">
                                        Contact sales for a quote
                                    </Typography>}
                                    {plan.price !== null && plan.price !== "custom" ? (
                                        <Typography variant="h5" fontWeight={700} color="#222" m={0} mx={0} display="flex" alignItems="center">
                                            {saleLive ? (
                                                <>
                                                    <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                                                        $<Typography component="span" fontWeight="inherit" fontSize={28}>
                                                            {plan.name === 'Live Webinars'
                                                                ? (planDuration === 'yearly' ? liveSelected.yearlySalePrice : liveSelected.monthly)
                                                                : plan.name === 'Automated'
                                                                    ? (planDuration === 'yearly' ? automatedSelected.yearlySalePrice : automatedSelected.monthly)
                                                                    : (planDuration === 'yearly' ? liveAutoSelected.yearlySalePrice : liveAutoSelected.monthly)
                                                            }
                                                        </Typography>
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center' }}>
                                                        $<Typography component="span" fontWeight="inherit" fontSize={28}>
                                                            {plan.price}
                                                        </Typography>
                                                    </span>
                                                </>
                                            ) : (
                                                <span style={{ display: 'flex', alignItems: 'center' }}>$<Typography component="span" fontWeight="inherit" fontSize={28}>{plan.price}</Typography></span>
                                            )}
                                            <Typography component="span" fontSize={14} color="#666666" fontWeight={400} ml={1}>{plan.priceLabel}</Typography>
                                        </Typography>
                                    ) : ""} */}
                    {/* 
                                    {plan.dropdown && plan.price !== null && plan.dropdown}

                                    {plan.price === null && plan.dropdown} */}
                    {plan.button}
                  </Box>
                ))}
            </Box>
            {/* Features Table */}
            <Box
              sx={{
                width: "100%",
                minWidth: 1100,
                background: "#fff",
                borderRadius: "0 0 12px 12px",
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                mt: 0,
              }}
            >
              {(showAllFeatures ? FEATURES : FEATURES.slice(0, 9)).map(
                (feature, i) =>
                  feature.section ? (
                    <Box
                      key={feature.section}
                      sx={{
                        width: "100%",
                        px: 4,
                        pt: "14px",
                        pb: "10px",
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        fontSize: 22,
                        background: "transparent",
                        letterSpacing: 0.2,
                        borderTop: "1px solid #E8EAF0",

                        "& + div": {
                          borderTop: "none",
                        },
                      }}
                    >
                      {feature.section}
                    </Box>
                  ) : (
                    <Box
                      key={feature.label}
                      display="flex"
                      alignItems="center"
                      sx={{
                        borderTop: i === 0 ? "none" : "1px solid #E8EAF0",
                        py: "10px",
                        px: 0,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 240,
                          pl: 4,
                          color: "#2B2B30",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          maxWidth: "20%",
                          width: "100%",
                        }}
                      >
                        {feature.label}
                        {/* {feature.info && (
                                                <Tooltip arrow title={feature.info}>
                                                    <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                                                        <InfoIcon />
                                                    </Box>
                                                </Tooltip>
                                            )} */}
                      </Box>
                      {Array.isArray(feature.plans) &&
                        feature.plans.map((val, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              flex: 1,
                              minWidth: 220,
                              textAlign: "center",
                              color:
                                typeof val === "string" ? "#333" : undefined,
                              fontWeight: typeof val === "string" ? 500 : 400,
                            }}
                          >
                            {val === true && (
                              <CheckIcon
                                sx={{ color: "#333333", fontSize: 19 }}
                              />
                            )}
                            {val === false && (
                              <Typography fontWeight={700}>-</Typography>
                            )}
                            {typeof val === "string" && val}
                          </Box>
                        ))}
                    </Box>
                  ),
              )}
              <Box
                sx={{
                  background: "#F0F0F0",
                  px: 3,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  border: "1px solid  #E8EAF0",
                  fontWeight: 500,
                  fontSize: "min(18px, 14px + 4 * (100vw - 320px) / 1600)",
                  color: "#333333",
                  userSelect: "none",
                  mt: 0,
                  "&:hover": {
                    background: "#E8EAF0",
                  },
                }}
                onClick={() => setShowAllFeatures((prev) => !prev)}
              >
                {showAllFeatures ? "Show less" : "Compare plans"}
                <Box
                  component="span"
                  display="flex"
                  sx={{
                    ml: 1,
                    transition: "transform 0.2s",
                    transform: showAllFeatures
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="8"
                    viewBox="0 0 11 8"
                    fill="none"
                  >
                    <path
                      d="M0.922687 1.50373L5.49992 6.49707L10.0771 1.50373"
                      stroke="#333333"
                      stroke-width="1.66445"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default PricingSectionV2;
